import { Location } from 'react-router';

import { Link } from '@/default-config';
import { CreditCardService } from '@/domain/card';

import { SearchParamsType } from '@components/app/routes';
import { LinkProducer, NavigateFunction, SearchParams } from '@domain/app';
import { Card, CreditCard, HardBlockReason, Session } from '@generated-types';
import { KortbetjeningText } from '@hooks/use-text';
import { getCardAccountNumber } from '@utils/account-utils';
import { isBusinessCard } from '@utils/card-type-utils';
import { Query } from '@utils/query-utils';

// it should not be used
export const getRootUrl = (linkProducer: LinkProducer) => linkProducer('HOME_PAGE')();

export const getCardQueryKeys = (card: Card | CreditCard) => ({
    [SearchParams.CARD_TYPE]: CreditCardService.isCreditCard(card) ? 'CREDIT' : card.type,
    [SearchParams.CARD_ID]: card.id,
});

// **** ------------------------------------- ****
// **** --------------- BLOCK --------------- ****
// **** ------------------------------------- ****

export const getSoftBlockUrl = (linkProducer: LinkProducer) => (card: Card) =>
    linkProducer('SOFT_BLOCK_CARD_CONFIRM_PAGE')({ cid: card.id, ctype: card.type });

export const navigateToSoftBlock = (navigate: NavigateFunction) => (card: Card) =>
    navigate('SOFT_BLOCK_CARD_CONFIRM_PAGE')({ cid: card.id, ctype: card.type });

export const getHardBlockUrl = (linkProducer: LinkProducer) => (card: Card, redirect?: boolean) =>
    linkProducer('SELECT_REASON_PAGE')({
        cid: card.id,
        ctype: card.type,
        ...(redirect ? { [SearchParams.CARD_DETAILS_REDIRECT]: redirect.toString() } : {}),
    });

export const navigateToHardBlock = (navigate: NavigateFunction) => (card: Card, redirect?: boolean) =>
    navigate('SELECT_REASON_PAGE')({
        cid: card.id,
        ctype: card.type,
        ...(redirect ? { [SearchParams.CARD_DETAILS_REDIRECT]: redirect.toString() } : {}),
    });

interface HardBlockRequest {
    card: Card;
    selectedReason: HardBlockReason;
    reorder: boolean;
    changedPin?: boolean;
}

const getHardBlockConfirmQuery = ({
    card,
    selectedReason,
    reorder,
    changedPin = false,
}: HardBlockRequest): SearchParamsType => {
    return {
        ...getCardQueryKeys(card),
        ...Query.getReasonSearchParams(selectedReason),
        ...(reorder ? Query.getReorderSearchParams(reorder) : {}),
        ...(changedPin ? Query.getChangedPinSearchParams(changedPin) : {}),
    };
};

export const navigateToHardBlockConfirm = (navigate: NavigateFunction) => (hardBlockRequest: HardBlockRequest) => {
    const query = getHardBlockConfirmQuery(hardBlockRequest);
    navigate('HARD_BLOCK_CARD_CONFIRM_PAGE')(query);
};

export const navigateToReorder = (navigate: NavigateFunction) => (card: Card, reason: HardBlockReason) => {
    navigate('SELECT_REORDER_CARD_PAGE')({ cid: card.id, ctype: card.type, reason });
};

// **** --------------------------------------- ****
// **** --------------- REORDER --------------- ****
// **** --------------------------------------- ****

export const navigateToPinChangeDecision = (navigate: NavigateFunction, location: Location) =>
    navigate('PIN_CHANGE_DECISION_PAGE', { keepParams: true, location })();

export const navigateToNewPinEntry = (navigate: NavigateFunction, location: Location) =>
    navigate('NEW_PIN_ENTRY_PAGE', { keepParams: true, location })();

// **** ------------------------------------------- ****
// **** --------------- ORDER DEBIT --------------- ****
// **** ------------------------------------------- ****

export const getOrderCardUrl = (linkProducer: LinkProducer) => linkProducer('ORDER_CARD_PAGE')();

// **** ------------------------------------------- ****
// **** ---------- CREDIT CARD APPLICATION -------- ****
// **** ------------------------------------------- ****

// NOTE: looks that we dont have this page
export const getCreditCardApplicationUrl = (session: Session, text: KortbetjeningText) => {
    const { creditCardApplicationUrlNative, creditCardApplicationUrl, creditCardApplicationUrlParamChange } = text;

    const url = session.channel === 'native' ? creditCardApplicationUrlNative : creditCardApplicationUrl;

    return `${url}${creditCardApplicationUrlParamChange}`;
};

export const getCreditCardApplicationUrlForCard = (card: Card, session: Session, text: KortbetjeningText) => {
    const { creditCardApplicationUrlParamBusiness, creditCardApplicationUrlParamGold } = text;

    const businessSuffix = isBusinessCard(card)
        ? creditCardApplicationUrlParamBusiness
        : creditCardApplicationUrlParamGold;

    const baseUrl = getCreditCardApplicationUrl(session, text);

    return `${baseUrl}${businessSuffix}`;
};

export const getCloseCreditAccountQuery = (card: Card) => ({
    [SearchParams.CLOSE_ACCOUNT_NUMBER]: getCardAccountNumber(card),
    [SearchParams.CARD_TYPE]: card.type,
    [SearchParams.CARD_ID]: card.id,
});

export const getVisaCalculatorUrl = (linkProducer: LinkProducer) => linkProducer('CURRENCY_CALCULATOR_PAGE')();

const cardIdQuery = (card: Card) => `${SearchParams.CARD_ID}=${card.id}`;

export const getReorderSelectedCardUrl = (card: Card, link: Link) => `${link.url}?${cardIdQuery(card)}`;

export const getBlockSelectedCardUrl = (linkProducer: LinkProducer) => (card: Card, redirect: boolean) =>
    linkProducer('SELECT_BLOCK_TYPE_PAGE')({
        cid: card.id,
        ctype: card.type,
        [SearchParams.CARD_DETAILS_REDIRECT]: redirect.toString(),
    });

export const getComplaintUrl = (linkProducer: LinkProducer) => linkProducer('COMPLAINT_PAGE')();

// NOTE encodedAccountId can be undefined here - needs to be fixed
export const getOrderCardPinDecisionUrl =
    (linkProducer: LinkProducer) => (accountNumber: string, accountId?: string) => {
        let encodedAccountId = accountId;
        if (accountId) {
            encodedAccountId = encodeURIComponent(accountId);
        }

        return linkProducer('ORDER_CARD_FORM_PIN_DECISION_PAGE')({
            kontoid: encodedAccountId,
            kontonummer: accountNumber,
        });
    };

export const getInsuranceProofUrl = (linkProducer: LinkProducer) => linkProducer('INSURANCE_PROOF_PAGE')();
